import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/common/ui/card'
import { Button } from '@/common/ui/button'
import { Input } from '@/common/ui/input'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/common/ui/dialog'
import { Check, HelpCircle } from 'lucide-react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateGeneralSetting from '@/modules/client-setting/api/hooks/useMutationUpdateGeneralSetting'
import { GetGeneralSettingResponse } from '@/modules/client-setting/type'
import { GeneralSettingQueryKeys } from '@/modules/client-setting/keys'

interface FontFamilyInputProps {
	data: GetGeneralSettingResponse
}

const FONT_FAMILY_REGEX = /^[A-Za-z0-9\s\-+]+$/

export default function FontFamilyInput({ data }: FontFamilyInputProps) {
	const queryClient = useQueryClient()
	const [fontFamily, setFontFamily] = useState(data?.fontFamily ?? '')
	const [isSaving, setIsSaving] = useState(false)
	const [isGuideOpen, setIsGuideOpen] = useState(false)
	const { mutateAsync: updateGeneralSetting } =
		useMutationUpdateGeneralSetting()

	const handleSave = async () => {
		const trimmed = fontFamily.trim()

		if (trimmed && !FONT_FAMILY_REGEX.test(trimmed)) {
			showErrorToast(
				'ชื่อฟอนต์มีอักขระไม่ถูกต้อง (อนุญาตเฉพาะ A-Z, a-z, 0-9, ช่องว่าง, ขีดกลาง, และเครื่องหมายบวก)',
			)
			return
		}

		try {
			setIsSaving(true)

			await updateGeneralSetting({
				payload: {
					...data,
					fontFamily: trimmed || undefined,
				},
			})

			queryClient.invalidateQueries({
				queryKey: [GeneralSettingQueryKeys.GetGeneralSetting],
			})

			if (trimmed) {
				toast.success(`ตั้งค่าฟอนต์ "${trimmed}" สำเร็จ`)
			} else {
				toast.success('รีเซ็ตฟอนต์เป็นค่าเริ่มต้นสำเร็จ')
			}
		} catch (error) {
			showErrorToast(error as Error)
		} finally {
			setIsSaving(false)
		}
	}

	const defaultFontName = 'IBM Plex Sans Thai'

	return (
		<Card>
			<CardHeader>
				<div className="flex items-center justify-between">
					<div className="space-y-1">
						<CardTitle>ตั้งค่าฟอนต์สำหรับเว็บไซต์</CardTitle>
						<CardDescription>
							ระบุชื่อฟอนต์ Google Fonts (เช่น Kanit, Noto Sans Thai, Sarabun)
						</CardDescription>
					</div>
					<Dialog open={isGuideOpen} onOpenChange={setIsGuideOpen}>
						<DialogTrigger asChild>
							<Button variant="outline" size="sm" className="shrink-0">
								<HelpCircle className="mr-1 h-4 w-4" />
								วิธีตั้งค่า
							</Button>
						</DialogTrigger>
						<DialogContent className="max-h-[80vh] overflow-y-auto sm:max-w-lg">
							<DialogHeader>
								<DialogTitle>วิธีตั้งค่าฟอนต์ Google Fonts</DialogTitle>
								<DialogDescription>
									ทำตามขั้นตอนด้านล่างเพื่อค้นหาชื่อฟอนต์ที่ต้องการ
								</DialogDescription>
							</DialogHeader>
							<div className="space-y-4 text-sm">
								<div className="space-y-2 rounded-lg border bg-muted/30 p-4">
									<p className="font-semibold">ขั้นตอนที่ 1</p>
									<p>
										เปิด{' '}
										<a
											href="https://fonts.google.com"
											target="_blank"
											rel="noopener noreferrer"
											className="text-primary underline"
										>
											fonts.google.com
										</a>
									</p>
								</div>

								<div className="space-y-2 rounded-lg border bg-muted/30 p-4">
									<p className="font-semibold">ขั้นตอนที่ 2</p>
									<p>
										ค้นหาฟอนต์ที่ต้องการ (แนะนำให้ค้นหาคำว่า &ldquo;Thai&rdquo;
										เพื่อดูฟอนต์ที่รองรับภาษาไทย)
									</p>
								</div>

								<div className="space-y-2 rounded-lg border bg-muted/30 p-4">
									<p className="font-semibold">ขั้นตอนที่ 3</p>
									<p>
										คลิกที่ฟอนต์ที่ต้องการ ตรวจสอบความถูกต้อง และคัดลอกชื่อฟอนต์
									</p>
								</div>

								<div className="space-y-2 rounded-lg border bg-muted/30 p-4">
									<p className="font-semibold">ขั้นตอนที่ 4</p>
									<p>
										นำชื่อฟอนต์ที่คัดลอกมา วางลงในช่องกรอกด้านล่าง แล้วกด{' '}
										<span className="font-semibold">บันทึก</span>
									</p>
								</div>

								<div className="space-y-1 rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-900 dark:bg-amber-950">
									<p className="text-sm font-semibold text-amber-800 dark:text-amber-200">
										ตัวอย่างชื่อฟอนต์ที่สามารถใช้ได้
									</p>
									<p className="text-xs text-amber-700 dark:text-amber-300">
										Kanit, Noto Sans Thai, Noto Sans Thai Looped, Sarabun,
										Chakra Petch, K2D, Mali, Athiti, Mitr, Niramit
									</p>
								</div>
							</div>

							<Button onClick={() => setIsGuideOpen(false)} className="w-full">
								เข้าใจแล้ว
							</Button>
						</DialogContent>
					</Dialog>
				</div>
			</CardHeader>
			<CardContent className="space-y-4">
				<div className="space-y-2">
					<Input
						value={fontFamily}
						onChange={(e) => setFontFamily(e.target.value)}
						placeholder={`เช่น Kanit หรือเว้นว่างเพื่อใช้ ${defaultFontName}`}
						disabled={isSaving}
					/>
					<p className="text-sm text-muted-foreground">
						ปัจจุบัน: {data?.fontFamily ?? defaultFontName}
					</p>
				</div>

				<Button
					type="button"
					onClick={handleSave}
					disabled={isSaving || fontFamily === (data?.fontFamily ?? '')}
					className="w-full"
				>
					<Check className="mr-2 h-4 w-4" />
					{isSaving ? 'กำลังบันทึก...' : 'บันทึก'}
				</Button>

				<p className="text-sm text-muted-foreground">
					เว้นว่างเพื่อใช้ฟอนต์เริ่มต้น {defaultFontName}
				</p>
			</CardContent>
		</Card>
	)
}
